import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { UserDocumentService } from '../services/userDocumentService';
import { DataSynchronizationService } from '../services/dataSynchronizationService';
import { UserDocument, CreateUserDocumentData, AccountData } from '../types/userDocument';
import { useAuth } from './AuthContext';

interface UserDocumentContextType {
  userDocument: UserDocument | null;
  isLoading: boolean;
  error: string | null;
  
  // Account-specific data
  demoAccountData: AccountData | null;
  realAccountData: AccountData | null;
  
  // Document operations
  createUserDocument: (data: CreateUserDocumentData) => Promise<void>;
  updateUserDocument: (updates: any) => Promise<void>;
  refreshUserDocument: () => Promise<void>;
  
  // Comprehensive data synchronization
  synchronizeAllData: () => Promise<void>;
  
  // Account-specific operations
  updateOpenPosition: (accountType: string, position: any) => Promise<void>;
  removeOpenPosition: (accountType: string, positionId: string) => Promise<void>;
  addClosedTrade: (accountType: string, trade: any) => Promise<void>;
  addTradingHistoryEntry: (accountType: string, entry: any) => Promise<void>;
  
  // Order operations
  updateOpenOrder: (accountType: string, order: any) => Promise<void>;
  removeOpenOrder: (accountType: string, orderId: string) => Promise<void>;
  
  // Account operations
  updateAccountSummary: (accountType: string, summary: any) => Promise<void>;
  addFinancialTransaction: (accountType: string, transaction: any) => Promise<void>;
  
  // Settings operations
  updateTradingSettings: (accountType: string, settings: any) => Promise<void>;
  updateRiskManagement: (accountType: string, riskManagement: any) => Promise<void>;
  updatePerformanceMetrics: (accountType: string, metrics: any) => Promise<void>;
  
  // AI Trading operations
  updateAITradingData: (accountType: string, aiData: any) => Promise<void>;
  updateManualTradingData: (accountType: string, manualData: any) => Promise<void>;
  
  // Utility functions
  clearError: () => void;
  getAccountData: (accountType: string) => AccountData | null;
}

const UserDocumentContext = createContext<UserDocumentContextType | undefined>(undefined);

export const useUserDocument = () => {
  const context = useContext(UserDocumentContext);
  if (context === undefined) {
    throw new Error('useUserDocument must be used within a UserDocumentProvider');
  }
  return context;
};

interface UserDocumentProviderProps {
  children: ReactNode;
}

export const UserDocumentProvider: React.FC<UserDocumentProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [userDocument, setUserDocument] = useState<UserDocument | null>(null);
  const [demoAccountData, setDemoAccountData] = useState<AccountData | null>(null);
  const [realAccountData, setRealAccountData] = useState<AccountData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user document when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserDocument();
    } else {
      setUserDocument(null);
    }
  }, [isAuthenticated, user]);

  const loadUserDocument = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      // Wait a bit for authentication to be fully established
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if user document exists
      const exists = await UserDocumentService.userDocumentExists(user.uid);
      
      if (!exists) {
        // Create new user document with demo and real accounts
        const createData: CreateUserDocumentData = {
          userId: user.uid,
          email: user.email || '',
          displayName: user.displayName || ''
        };
        
        await UserDocumentService.createUserDocument(createData);
      }
      
      // Comprehensive data synchronization
      await synchronizeAllData();
      
    } catch (err: any) {
      console.error('❌ Error loading user document:', err);
      
      // Handle specific Firebase errors
      if (err.message?.includes('Missing or insufficient permissions')) {
        setError('Permission denied. Please check your Firestore security rules.');
      } else if (err.message?.includes('User must be authenticated')) {
        setError('Authentication required. Please log in again.');
      } else {
        setError(err.message || 'Failed to load user document');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Comprehensive data synchronization function
  const synchronizeAllData = async () => {
    if (!user) return;

    try {
      
      const syncResult = await DataSynchronizationService.synchronizeAllUserData(user.uid);
      
      if (syncResult.success && syncResult.data) {
        // Update user document
        const document = await UserDocumentService.getUserDocument(user.uid);
        setUserDocument(document);
        
        // Update account-specific data
        setDemoAccountData(syncResult.data.demo);
        setRealAccountData(syncResult.data.real);
        
      } else {
        throw new Error(syncResult.error || 'Data synchronization failed');
      }
    } catch (error: any) {
      console.error('❌ Data synchronization failed:', error);
      setError(error.message || 'Failed to synchronize data');
    }
  };

  const createUserDocument = async (data: CreateUserDocumentData) => {
    setIsLoading(true);
    setError(null);

    try {
      await UserDocumentService.createUserDocument(data);
      await loadUserDocument();
    } catch (err: any) {
      console.error('Error creating user document:', err);
      setError(err.message || 'Failed to create user document');
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserDocument = async (updates: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updateUserDocument(user.uid, updates);
      
      // Update local state
      if (userDocument) {
        setUserDocument({
          ...userDocument,
          ...updates,
          lastUpdated: new Date()
        });
      }
    } catch (err: any) {
      console.error('Error updating user document:', err);
      setError(err.message || 'Failed to update user document');
    }
  };

  const refreshUserDocument = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      await synchronizeAllData();
    } catch (err: any) {
      console.error('Error refreshing user document:', err);
      setError(err.message || 'Failed to refresh user document');
    } finally {
      setIsLoading(false);
    }
  };

  // Get account data for specific account type
  const getAccountData = (accountType: string): AccountData | null => {
    if (accountType === 'demo') return demoAccountData;
    if (accountType === 'real') return realAccountData;
    return null;
  };

  const updateOpenPosition = async (accountType: string, position: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updateOpenPosition(user.uid, accountType, position);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        const updatedPositions = [...(demoAccountData.openPositions || [])];
        const existingIndex = updatedPositions.findIndex(p => p.id === position.id);
        
        if (existingIndex >= 0) {
          updatedPositions[existingIndex] = position;
        } else {
          updatedPositions.push(position);
        }
        
        setDemoAccountData({
          ...demoAccountData,
          openPositions: updatedPositions,
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        const updatedPositions = [...(realAccountData.openPositions || [])];
        const existingIndex = updatedPositions.findIndex(p => p.id === position.id);
        
        if (existingIndex >= 0) {
          updatedPositions[existingIndex] = position;
        } else {
          updatedPositions.push(position);
        }
        
        setRealAccountData({
          ...realAccountData,
          openPositions: updatedPositions,
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error updating open position:', err);
      setError(err.message || 'Failed to update open position');
    }
  };

  const removeOpenPosition = async (accountType: string, positionId: string) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.removeOpenPosition(user.uid, accountType, positionId);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        const updatedPositions = (demoAccountData.openPositions || []).filter(p => p.id !== positionId);
        setDemoAccountData({
          ...demoAccountData,
          openPositions: updatedPositions,
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        const updatedPositions = (realAccountData.openPositions || []).filter(p => p.id !== positionId);
        setRealAccountData({
          ...realAccountData,
          openPositions: updatedPositions,
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error removing open position:', err);
      setError(err.message || 'Failed to remove open position');
    }
  };

  const updateOpenOrder = async (accountType: string, order: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updateOpenOrder(user.uid, accountType, order);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        const updatedOrders = [...(demoAccountData.openOrders || [])];
        const existingIndex = updatedOrders.findIndex((o: any) => o.id === order.id);
        
        if (existingIndex >= 0) {
          updatedOrders[existingIndex] = order;
        } else {
          updatedOrders.push(order);
        }
        
        setDemoAccountData({
          ...demoAccountData,
          openOrders: updatedOrders,
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        const updatedOrders = [...(realAccountData.openOrders || [])];
        const existingIndex = updatedOrders.findIndex((o: any) => o.id === order.id);
        
        if (existingIndex >= 0) {
          updatedOrders[existingIndex] = order;
        } else {
          updatedOrders.push(order);
        }
        
        setRealAccountData({
          ...realAccountData,
          openOrders: updatedOrders,
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error updating open order:', err);
      setError(err.message || 'Failed to update open order');
    }
  };

  const removeOpenOrder = async (accountType: string, orderId: string) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.removeOpenOrder(user.uid, accountType, orderId);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        const updatedOrders = (demoAccountData.openOrders || []).filter((o: any) => o.id !== orderId);
        setDemoAccountData({
          ...demoAccountData,
          openOrders: updatedOrders,
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        const updatedOrders = (realAccountData.openOrders || []).filter((o: any) => o.id !== orderId);
        setRealAccountData({
          ...realAccountData,
          openOrders: updatedOrders,
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error removing open order:', err);
      setError(err.message || 'Failed to remove open order');
    }
  };

  const addClosedTrade = async (accountType: string, trade: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.addClosedTrade(user.uid, accountType, trade);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        const updatedTrades = [...(demoAccountData.closedTrades || []), trade];
        setDemoAccountData({
          ...demoAccountData,
          closedTrades: updatedTrades,
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        const updatedTrades = [...(realAccountData.closedTrades || []), trade];
        setRealAccountData({
          ...realAccountData,
          closedTrades: updatedTrades,
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error adding closed trade:', err);
      setError(err.message || 'Failed to add closed trade');
    }
  };

  const addTradingHistoryEntry = async (accountType: string, entry: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.addTradingHistoryEntry(user.uid, accountType, entry);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        const updatedHistory = [...(demoAccountData.tradingHistory || []), entry];
        setDemoAccountData({
          ...demoAccountData,
          tradingHistory: updatedHistory,
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        const updatedHistory = [...(realAccountData.tradingHistory || []), entry];
        setRealAccountData({
          ...realAccountData,
          tradingHistory: updatedHistory,
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error adding trading history entry:', err);
      setError(err.message || 'Failed to add trading history entry');
    }
  };

  const updateAccountSummary = async (accountType: string, summary: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updateAccountSummary(user.uid, accountType, summary);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        setDemoAccountData({
          ...demoAccountData,
          accountSummary: {
            ...demoAccountData.accountSummary,
            ...summary
          },
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        setRealAccountData({
          ...realAccountData,
          accountSummary: {
            ...realAccountData.accountSummary,
            ...summary
          },
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error updating account summary:', err);
      setError(err.message || 'Failed to update account summary');
    }
  };

  const addFinancialTransaction = async (accountType: string, transaction: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.addFinancialTransaction(user.uid, accountType, transaction);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        const updatedTransactions = [...(demoAccountData.financialTransactions || []), transaction];
        setDemoAccountData({
          ...demoAccountData,
          financialTransactions: updatedTransactions,
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        const updatedTransactions = [...(realAccountData.financialTransactions || []), transaction];
        setRealAccountData({
          ...realAccountData,
          financialTransactions: updatedTransactions,
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error adding financial transaction:', err);
      setError(err.message || 'Failed to add financial transaction');
    }
  };

  const updateTradingSettings = async (accountType: string, settings: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updateTradingSettings(user.uid, accountType, settings);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        setDemoAccountData({
          ...demoAccountData,
          tradingSettings: {
            ...demoAccountData.tradingSettings,
            ...settings
          },
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        setRealAccountData({
          ...realAccountData,
          tradingSettings: {
            ...realAccountData.tradingSettings,
            ...settings
          },
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error updating trading settings:', err);
      setError(err.message || 'Failed to update trading settings');
    }
  };

  const updateRiskManagement = async (accountType: string, riskManagement: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updateRiskManagement(user.uid, accountType, riskManagement);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        setDemoAccountData({
          ...demoAccountData,
          riskManagement: {
            ...demoAccountData.riskManagement,
            ...riskManagement
          },
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        setRealAccountData({
          ...realAccountData,
          riskManagement: {
            ...realAccountData.riskManagement,
            ...riskManagement
          },
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error updating risk management:', err);
      setError(err.message || 'Failed to update risk management');
    }
  };

  const updatePerformanceMetrics = async (accountType: string, metrics: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updatePerformanceMetrics(user.uid, accountType, metrics);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        setDemoAccountData({
          ...demoAccountData,
          performanceMetrics: {
            ...demoAccountData.performanceMetrics,
            ...metrics
          },
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        setRealAccountData({
          ...realAccountData,
          performanceMetrics: {
            ...realAccountData.performanceMetrics,
            ...metrics
          },
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error updating performance metrics:', err);
      setError(err.message || 'Failed to update performance metrics');
    }
  };

  const updateAITradingData = async (accountType: string, aiData: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updateAITradingData(user.uid, accountType, aiData);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        setDemoAccountData({
          ...demoAccountData,
          aiTradingData: {
            ...demoAccountData.aiTradingData,
            ...aiData
          },
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        setRealAccountData({
          ...realAccountData,
          aiTradingData: {
            ...realAccountData.aiTradingData,
            ...aiData
          },
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error updating AI trading data:', err);
      setError(err.message || 'Failed to update AI trading data');
    }
  };

  const updateManualTradingData = async (accountType: string, manualData: any) => {
    if (!user) return;

    setError(null);

    try {
      await UserDocumentService.updateManualTradingData(user.uid, accountType, manualData);
      
      // Update local state
      if (accountType === 'demo' && demoAccountData) {
        setDemoAccountData({
          ...demoAccountData,
          manualTradingData: {
            ...demoAccountData.manualTradingData,
            ...manualData
          },
          accountMetadata: {
            ...demoAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      } else if (accountType === 'real' && realAccountData) {
        setRealAccountData({
          ...realAccountData,
          manualTradingData: {
            ...realAccountData.manualTradingData,
            ...manualData
          },
          accountMetadata: {
            ...realAccountData.accountMetadata,
            lastActive: new Date()
          }
        });
      }
    } catch (err: any) {
      console.error('Error updating manual trading data:', err);
      setError(err.message || 'Failed to update manual trading data');
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: UserDocumentContextType = {
    userDocument,
    isLoading,
    error,
    demoAccountData,
    realAccountData,
    createUserDocument,
    updateUserDocument,
    refreshUserDocument,
    synchronizeAllData,
    updateOpenPosition,
    removeOpenPosition,
    updateOpenOrder,
    removeOpenOrder,
    addClosedTrade,
    addTradingHistoryEntry,
    updateAccountSummary,
    addFinancialTransaction,
    updateTradingSettings,
    updateRiskManagement,
    updatePerformanceMetrics,
    updateAITradingData,
    updateManualTradingData,
    clearError,
    getAccountData,
  };

  return (
    <UserDocumentContext.Provider value={value}>
      {children}
    </UserDocumentContext.Provider>
  );
};
